import { ArrowBack } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Chip, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import { getFunnelById, getMessageHistory } from 'API/linkedin-funnel';
import { getAllFunnelContacts } from 'API/linkedin-funnel-search';
import { ProductGroups } from 'enums';
import { FunnelContact } from 'features/co-pilot/co-pilot.types';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { formatDateTime } from 'utils/date.utils';

import { ContactFilter } from '../campaign-builder/campaign-builder.types';
import getLocalizedText from '../translationFileProduct';
import { StatisticsAccordionStates } from './campaign-overview.types';
import ContactDrawer from '../campaign-builder/campaign-building-panel/campaign-pipeline/contact-preview-drawer/contact-drawer';
import useContactDrawer from '../campaign-builder/use-contact-drawer';
import MessageHistoryPanel from '../campaign-statistics/message-history-panel';
import { LinkedinContact } from '../campaign-statistics/campaign-statistics-types';

/** A campaign step as returned by `getFunnelById` (see FunnelStep in co-pilot.types). */
interface DetailStep {
  index: number;
  functionName?: string;
  waitTillNext?: number;
  waitDays?: number;
  prompt?: string;
}

interface Props {
  campaignId: string;
  /** Navigates back to the campaign list. */
  onBack(): void;
  /** Opens the campaign builder for this campaign. */
  onEdit?(): void;
  mapLinkedinAccountIdToName?(linkedinAccountId: string): string;
  /** Only used for the product label in the header band. */
  productGroup?: ProductGroups;
}

/** Index used for the synthetic "finished" bucket, mirroring campaign-flow-table. */
const FINISHED_STEP_INDEX = -1;

/**
 * Steps that only branch or wait produce no outbound touch. They stay in the tree
 * (the numbering has to match what the user configured) but are visually muted.
 */
const PASSIVE_FUNCTIONS = new Set([
  'waitStep',
  'wait',
  'checkReply',
  'checkIfConnected',
  'checkIfConnectedUntil',
  'checkIfGeneratedEmailAddress',
  'checkIsPolite',
]);

const formatWait = (seconds?: number, days?: number): string | null => {
  if (!_.isNil(days) && days > 0) {
    return `${days}d`;
  }
  if (_.isNil(seconds) || seconds <= 0) {
    return null;
  }
  const d = Math.floor(seconds / (24 * 60 * 60));
  const h = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60));
  const m = Math.floor((seconds % (60 * 60)) / 60);
  return [d ? `${d}d` : null, h ? `${h}h` : null, m ? `${m}m` : null].filter(Boolean).join(' ') || null;
};

const CampaignDetailPage: React.FC<Props> = ({ campaignId, onBack, onEdit, mapLinkedinAccountIdToName, productGroup }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [campaign, setCampaign] = useState<Record<string, any> | null>(null);
  const [contacts, setContacts] = useState<FunnelContact[]>([]);
  const [messageHistory, setMessageHistory] = useState<LinkedinContact[]>([]);
  const { contactDrawer, handleOpenContactDrawer, handleUpdateDrawerContactIsActive, handleCloseContactDrawer } = useContactDrawer();
  const [selectedStep, setSelectedStep] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<StatisticsAccordionStates>(StatisticsAccordionStates.FUNNEL);

  /** Steps are translated through the same key set the flow table uses. */
  const stepLabel = useCallback(
    (functionName?: string): string => {
      if (!functionName) {
        return t('step', 'Schritt');
      }
      const aliases: Record<string, string> = {
        checkIfConnected: 'connectedOnLinkedIn',
        checkIfConnectedUntil: 'contactRequestAccepted',
        checkIfGeneratedEmailAddress: 'hasEmailAddress',
        waitStep: 'waitingTime',
      };
      const key = aliases[functionName] || functionName;
      return t(key, functionName);
    },
    [t]
  );

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const [funnelRes, activeRes, finishedRes, historyRes] = await Promise.all([
          getFunnelById(campaignId),
          getAllFunnelContacts(campaignId, { filter: ContactFilter.IS_ACTIVE, limit: 10000, offset: 0 }),
          getAllFunnelContacts(campaignId, { filter: ContactFilter.IS_FINISHED, limit: 10000, offset: 0 }),
          getMessageHistory([campaignId], 'all'),
        ]);

        if (cancelled) {
          return;
        }

        setCampaign(funnelRes && !(funnelRes as any).error ? (funnelRes as Record<string, any>) : null);

        // The contacts endpoint answers with `{ error, data }` on failure — that shape
        // carries no `funnelContacts`, so every access has to be guarded.
        const merge = (res: any): FunnelContact[] =>
          res && !res.error && Array.isArray(res.funnelContacts) ? res.funnelContacts : [];
        const merged = _.uniqBy([...merge(activeRes), ...merge(finishedRes)], (c) => c.linkedInURL || c._id);
        setContacts(merged);
        setMessageHistory(Array.isArray(historyRes) ? historyRes : []);
      } catch {
        if (!cancelled) {
          setCampaign(null);
          setContacts([]);
          setMessageHistory([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [campaignId]);

  const summary = (campaign?.funnelSummary || {}) as Record<string, number>;

  const steps: DetailStep[] = useMemo(() => {
    const raw = Array.isArray(campaign?.steps) ? (campaign!.steps as DetailStep[]) : [];
    return [...raw].filter((s) => !_.isNil(s?.index)).sort((a, b) => a.index - b.index);
  }, [campaign]);

  /** How many contacts currently sit on each step; finished ones go to the -1 bucket. */
  const countsByStep = useMemo(() => {
    const counts: Record<number, number> = {};
    contacts.forEach((c) => {
      const idx = c.isFinished ? FINISHED_STEP_INDEX : c.stepIndex || 0;
      counts[idx] = (counts[idx] || 0) + 1;
    });
    return counts;
  }, [contacts]);

  const funnelStages = useMemo(() => {
    const total = summary.totalContactsCount || 0;
    return [
      { key: 'totalContacts', label: t('totalContacts', 'Kontakte'), value: total, base: null as number | null },
      {
        key: 'connectionRequests',
        label: t('sentConnectionRequests', 'Anfragen gesendet'),
        value: summary.sentConnectionRequestsCount || 0,
        base: total,
      },
      {
        key: 'accepted',
        label: t('acceptedConnectionRequests', 'Angenommen'),
        value: summary.acceptedConnectionRequestsCount || 0,
        base: summary.sentConnectionRequestsCount || 0,
      },
      {
        key: 'contacted',
        label: t('messagesSent', 'Nachrichten gesendet'),
        value: summary.contactedContactsCount || 0,
        base: summary.acceptedConnectionRequestsCount || 0,
      },
      {
        key: 'replies',
        label: t('replies', 'Antworten'),
        value: summary.messageReplyCount || 0,
        base: summary.contactedContactsCount || 0,
      },
    ];
  }, [summary, t]);

  const selected = useMemo(
    () => (_.isNil(selectedStep) ? null : steps.find((s) => s.index === selectedStep) || null),
    [selectedStep, steps]
  );

  const selectedContacts = useMemo(() => {
    if (_.isNil(selectedStep)) {
      return [];
    }
    return contacts
      .filter((c) => (c.isFinished ? FINISHED_STEP_INDEX : c.stepIndex || 0) === selectedStep)
      .slice(0, 50);
  }, [contacts, selectedStep]);

  const accountName =
    campaign?.linkedinAccount && mapLinkedinAccountIdToName
      ? mapLinkedinAccountIdToName(campaign.linkedinAccount)
      : null;

  const productLogo = _.isNil(productGroup)
    ? null
    : productGroup === ProductGroups.HR_BOOSTER
      ? '/dashboard/images/logos/hr_booster_logo_short.svg'
      : productGroup === ProductGroups.LEAD_BOOSTER
        ? '/dashboard/images/logos/lead_booster_logo_short.svg'
        : '/dashboard/images/logos/product_logo_white.svg';

  const productLabel = _.isNil(productGroup)
    ? null
    : productGroup === ProductGroups.HR_BOOSTER
      ? t('hrBooster')
      : productGroup === ProductGroups.LEAD_BOOSTER
        ? t('leadBooster')
        : t('psychologicalAi');

  /** The four metric columns of the header band, mirroring the campaign list. */
  const metrics = useMemo(() => {
    const contactsLabel = getLocalizedText('contacts', productGroup ?? ProductGroups.HR_BOOSTER, t);
    const total = summary.totalContactsCount || 0;
    const contacted = summary.contactedContactsCount || 0;
    return [
      { key: 'inCampaign', label: contactsLabel, sub: t('inCampaign'), value: total, total: undefined as number | undefined },
      { key: 'inFinalList', label: contactsLabel, sub: t('inFinalList'), value: summary.activeContactsCount || 0, total },
      { key: 'contacted', label: t('contacted'), sub: contactsLabel, value: contacted, total: undefined as number | undefined },
      {
        key: 'replied',
        label: `${t('accepted')} ${t('or')}`,
        sub: t('replied'),
        value: summary.replyAndAcceptCount || summary.messageReplyCount || 0,
        total: contacted,
      },
    ];
  }, [summary, productGroup, t]);

  const tabs = useMemo(
    () => [
      { key: StatisticsAccordionStates.FUNNEL, label: t('funnel') },
      { key: StatisticsAccordionStates.MESSAGE_HISTORY, label: t('messageHistory') },
      { key: StatisticsAccordionStates.CONVERSIONS, label: t('conversionsAndStatistics') },
    ],
    [t]
  );

  const rate = (value: number, base: number | undefined) => (!_.isNil(base) && base > 0 ? `${((value / base) * 100).toFixed(2)} %` : null);

  return (
    <Box className={classes.page}>
      <Stack className={classes.backButton} direction={'row'} alignItems={'center'} gap={1} onClick={onBack}>
        <IconButton className={classes.iconButton}>
          <ArrowBack />
        </IconButton>
        <Typography className={classes.iconButtonText}>{t('backToAllCampaigns', 'Zurück zur Übersicht')}</Typography>
      </Stack>

      {loading ? (
        <Stack alignItems={'center'} justifyContent={'center'} sx={{ py: 8 }}>
          <CircularProgress size={28} />
        </Stack>
      ) : !campaign ? (
        <Stack alignItems={'center'} justifyContent={'center'} sx={{ py: 8 }} gap={1}>
          <Typography className={classes.emptyText}>
            {t('campaignNotFound', 'Diese Kampagne konnte nicht geladen werden.')}
          </Typography>
        </Stack>
      ) : (
        <>
          {/* One white header band instead of separate title and metric cards. */}
          <Box className={classes.headerBand}>
            <div className={classes.headerIdentity}>
              {productLabel ? (
                <Stack direction={'row'} alignItems={'center'} gap={'10px'}>
                  {productLogo ? <img src={productLogo} alt="logo" className={classes.logo} /> : null}
                  <Typography className={classes.productLabel}>{productLabel}</Typography>
                </Stack>
              ) : null}

              <Stack direction={'row'} alignItems={'center'} gap={1} flexWrap={'wrap'}>
                <Typography className={classes.title}>{campaign.name || t('campaign', 'Kampagne')}</Typography>
                {onEdit ? (
                  <IconButton className={classes.iconButton} onClick={onEdit}>
                    <EditIcon />
                  </IconButton>
                ) : null}
              </Stack>

              {campaign.status ? (
                <Chip size={'small'} label={String(t(campaign.status, campaign.status))} className={classes.statusChip} />
              ) : null}

              <Stack direction={'row'} gap={4} flexWrap={'wrap'} className={classes.headerMeta}>
                {accountName ? (
                  <Stack gap={'2px'}>
                    <Typography className={classes.metaLabel}>{t('campaignLead')}</Typography>
                    <Typography className={cx(classes.metaValue, 'highlight')}>{accountName}</Typography>
                  </Stack>
                ) : null}
                <Stack gap={'2px'}>
                  <Typography className={classes.metaLabel}>{t('createDate')}</Typography>
                  <Typography className={classes.metaValue}>
                    {campaign.createDate ? formatDateTime(campaign.createDate, 'Pp') : '-'}
                  </Typography>
                </Stack>
                {campaign.updateDate ? (
                  <Stack gap={'2px'}>
                    <Typography className={classes.metaLabel}>{t('lastUpdate', 'Letzte Aktualisierung')}</Typography>
                    <Typography className={classes.metaValue}>{formatDateTime(campaign.updateDate, 'Pp')}</Typography>
                  </Stack>
                ) : null}
              </Stack>
            </div>

            <div className={classes.headerMetrics}>
              {metrics.map((metric) => (
                <div key={metric.key} className={classes.metric}>
                  <Typography className={classes.metricLabel}>{metric.label}</Typography>
                  <Typography className={cx(classes.metricLabel, 'sub')}>{metric.sub}</Typography>
                  <Stack direction={'row'} alignItems={'baseline'} gap={'6px'} className={classes.metricValueRow}>
                    <Typography className={classes.metricValue}>{metric.value}</Typography>
                    {!_.isNil(metric.total) && metric.total > 0 ? (
                      <Typography className={classes.metricTotal}>/ {metric.total}</Typography>
                    ) : null}
                  </Stack>
                  <Typography className={classes.metricRate}>{rate(metric.value, metric.total) || ' '}</Typography>
                </div>
              ))}
            </div>
          </Box>

          {/* Segmented tab bar replacing the stacked sections. */}
          <div className={classes.tabBar}>
            {tabs.map((tab) => (
              <div
                key={tab.key}
                role={'button'}
                tabIndex={0}
                className={cx(classes.tab, activeTab === tab.key && 'active')}
                onClick={() => setActiveTab(tab.key)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    setActiveTab(tab.key);
                  }
                }}
              >
                {tab.label}
              </div>
            ))}
          </div>

          {/* Step tree */}
          <Box className={cx(classes.card, activeTab !== StatisticsAccordionStates.FUNNEL && classes.hidden)}>
            <Typography className={classes.cardTitle}>{t('campaignSteps', 'Kampagnenschritte')}</Typography>
            {steps.length === 0 ? (
              <Typography className={classes.emptyText} sx={{ mt: 1.5 }}>
                {t('noStepsConfigured', 'Für diese Kampagne sind keine Schritte hinterlegt.')}
              </Typography>
            ) : (
              <Stack sx={{ mt: 1.5 }}>
                {steps.map((step, position) => {
                  const count = countsByStep[step.index] || 0;
                  const wait = formatWait(step.waitTillNext, step.waitDays);
                  const isPassive = PASSIVE_FUNCTIONS.has(step.functionName || '');
                  return (
                    <Stack
                      key={`${step.index}-${position}`}
                      direction={'row'}
                      alignItems={'center'}
                      gap={2}
                      className={cx(classes.stepRow, selectedStep === step.index && classes.stepRowActive)}
                      onClick={() => setSelectedStep(selectedStep === step.index ? null : step.index)}
                    >
                      <Box className={cx(classes.stepNumber, isPassive && classes.stepNumberPassive)}>
                        {position + 1}
                      </Box>
                      <Stack sx={{ flex: 1, minWidth: 0 }}>
                        <Typography className={classes.stepLabel}>{stepLabel(step.functionName)}</Typography>
                        {wait ? <Typography className={classes.stepMeta}>{wait}</Typography> : null}
                      </Stack>
                      <Chip size={'small'} label={count} className={classes.stepChip} />
                    </Stack>
                  );
                })}
                {/* Finished bucket — contacts that ran through the whole campaign. */}
                <Stack
                  direction={'row'}
                  alignItems={'center'}
                  gap={2}
                  className={cx(
                    classes.stepRow,
                    selectedStep === FINISHED_STEP_INDEX && classes.stepRowActive
                  )}
                  onClick={() =>
                    setSelectedStep(selectedStep === FINISHED_STEP_INDEX ? null : FINISHED_STEP_INDEX)
                  }
                >
                  <Box className={cx(classes.stepNumber, classes.stepNumberPassive)}>✓</Box>
                  <Stack sx={{ flex: 1, minWidth: 0 }}>
                    <Typography className={classes.stepLabel}>{t('finished', 'Abgeschlossen')}</Typography>
                  </Stack>
                  <Chip
                    size={'small'}
                    label={countsByStep[FINISHED_STEP_INDEX] || 0}
                    className={classes.stepChip}
                  />
                </Stack>
              </Stack>
            )}

            {/* Detail for the selected step */}
            {!_.isNil(selectedStep) ? (
              <Box className={classes.stepDetail}>
                {selected?.prompt ? (
                  <>
                    <Typography className={classes.stepDetailTitle}>{t('message', 'Nachricht')}</Typography>
                    <Typography className={classes.stepDetailBody}>{selected.prompt}</Typography>
                  </>
                ) : null}
                <Typography className={classes.stepDetailTitle} sx={{ mt: selected?.prompt ? 2 : 0 }}>
                  {t('contacts', 'Kontakte')}
                </Typography>
                {selectedContacts.length === 0 ? (
                  <Typography className={classes.emptyText}>
                    {t('noContactsOnStep', 'Aktuell keine Kontakte auf diesem Schritt.')}
                  </Typography>
                ) : (
                  <Stack gap={0.5} sx={{ mt: 0.5 }}>
                    {selectedContacts.map((c, i) => (
                      <Typography key={c._id || c.linkedInURL || i} className={classes.contactRow}>
                        {c.fullName || [c.firstName, c.lastName].filter(Boolean).join(' ') || c.linkedInURL}
                      </Typography>
                    ))}
                  </Stack>
                )}
              </Box>
            ) : null}
          </Box>

          {/* Conversions & statistics */}
          <Box className={cx(classes.card, activeTab !== StatisticsAccordionStates.CONVERSIONS && classes.hidden)}>
            <Typography className={classes.cardTitle}>{t('funnel', 'Funnel')}</Typography>
            <Stack direction={'row'} gap={1} flexWrap={'wrap'} sx={{ mt: 1.5 }}>
              {funnelStages.map((stage) => (
                <Box key={stage.key} className={classes.stage}>
                  <Typography className={classes.stageValue}>{stage.value}</Typography>
                  <Typography className={classes.stageLabel}>{stage.label}</Typography>
                  {rate(stage.value, stage.base ?? undefined) ? (
                    <Typography className={classes.stageRate}>{rate(stage.value, stage.base ?? undefined)}</Typography>
                  ) : null}
                </Box>
              ))}
            </Stack>
          </Box>

          {/* Message history — same table as on the statistics page */}
          <Box className={cx(classes.card, activeTab !== StatisticsAccordionStates.MESSAGE_HISTORY && classes.hidden)}>
            <Typography className={classes.cardTitle}>
              {t('messageHistory')} ({summary.messageReplyCount || 0} {t('replies', 'Antworten')})
            </Typography>
            {messageHistory.length === 0 ? (
              <Typography className={classes.emptyText} sx={{ mt: 1.5 }}>
                {t('noRepliesYet', 'Noch keine Antworten erfasst.')}
              </Typography>
            ) : (
              <MessageHistoryPanel
                messageHistory={messageHistory}
                timeZone="Europe/Berlin"
                contactDrawerLoading={contactDrawer.loading}
                handleOpenContactDrawer={handleOpenContactDrawer}
                funnelIdsForExport={[campaignId]}
              />
            )}
          </Box>

          <ContactDrawer
            open={contactDrawer.open}
            productGroup={productGroup ?? ProductGroups.HR_BOOSTER}
            funnelId={contactDrawer.funnelId}
            contactIndex={contactDrawer.contactIndex}
            profileId={contactDrawer.profileId}
            aiJudgement={contactDrawer.aiJudgement}
            aiJudgementExplanation={contactDrawer.aiJudgementExplanation}
            assessment={contactDrawer.assessment}
            percentiles={contactDrawer.percentiles}
            isActive={contactDrawer.isActive}
            contactDetails={contactDrawer.contactDetails}
            aboutContact={contactDrawer.aboutContact}
            aboutCompany={contactDrawer.aboutCompany}
            handleUpdateDrawerContactIsActive={handleUpdateDrawerContactIsActive}
            handleCloseDrawer={handleCloseContactDrawer}
          />
        </>
      )}
    </Box>
  );
};

export default CampaignDetailPage;

const useStyles = makeStyles()((theme) => ({
  page: {
    padding: '16px 32px',
    overflow: 'auto',
    height: '100%',
  },
  backButton: {
    cursor: 'pointer',
    width: 'fit-content',
  },
  iconButton: {
    padding: '4px',
    background: '#FFF',
    '& .MuiSvgIcon-root': {
      fontSize: theme.typography.pxToRem(16),
      color: theme.palette.primary.main,
    },
    '&:hover': {
      background: theme.palette.action.hover,
    },
  },
  iconButtonText: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.primary.main,
  },
  title: {
    fontSize: theme.typography.pxToRem(24),
    fontWeight: 500,
    letterSpacing: '-0.2px',
    color: theme.palette.primary.main,
  },
  statusChip: {
    width: 'fit-content',
    borderRadius: '10px',
    background: theme.palette.backgrounds.main,
    fontSize: theme.typography.pxToRem(14),
    textTransform: 'capitalize',
  },
  /** One white band carrying identity and the four metric columns. */
  headerBand: {
    background: '#FFF',
    borderRadius: '15px',
    display: 'flex',
    alignItems: 'stretch',
    marginTop: '16px',
    marginBottom: '20px',
  },
  headerIdentity: {
    flex: 1.1,
    minWidth: 0,
    padding: '24px 28px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  logo: {
    width: '24px',
    height: 'auto',
  },
  productLabel: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 500,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: theme.palette.greys.medium,
  },
  headerMeta: {
    marginTop: 'auto',
  },
  metaLabel: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
  metaValue: {
    fontSize: theme.typography.pxToRem(14),
    '&.highlight': {
      color: theme.palette.primary.main,
    },
  },
  headerMetrics: {
    flex: 2,
    minWidth: 0,
    display: 'flex',
    alignItems: 'stretch',
  },
  metric: {
    flex: 1,
    minWidth: 0,
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    borderRight: `1px solid ${theme.palette.divider}`,
    '&:last-of-type': {
      borderRight: 'none',
    },
  },
  metricLabel: {
    fontSize: theme.typography.pxToRem(13),
    lineHeight: 1.35,
    color: theme.palette.greys.medium,
    '&.sub': {
      marginTop: '-6px',
    },
  },
  metricValueRow: {
    marginTop: 'auto',
  },
  metricValue: {
    fontSize: theme.typography.pxToRem(28),
    fontWeight: 500,
    fontVariantNumeric: 'tabular-nums',
  },
  metricTotal: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.greys.medium,
  },
  metricRate: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
    minHeight: '18px',
  },
  /** Segmented control — the sections below are tab panels. */
  tabBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    background: '#FFF',
    borderRadius: '12px',
    padding: '6px',
    width: 'fit-content',
    marginBottom: '20px',
  },
  tab: {
    padding: '8px 18px',
    borderRadius: '8px',
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.greys.dark,
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    '&:hover': {
      background: theme.palette.backgrounds.main_new,
    },
    '&.active': {
      background: theme.palette.backgrounds.main,
      color: theme.palette.primary.main,
      fontWeight: 500,
    },
  },
  hidden: {
    display: 'none',
  },
  card: {
    background: '#FFF',
    borderRadius: '15px',
    padding: '24px',
    marginBottom: '16px',
  },
  cardTitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 600,
  },
  stage: {
    flex: '1 1 140px',
    minWidth: '120px',
    background: '#f6f6fe',
    borderRadius: '10px',
    padding: '12px 14px',
  },
  stageValue: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
  },
  stageLabel: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.secondary,
  },
  stageRate: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.primary.main,
    marginTop: '2px',
  },
  stepRow: {
    padding: '10px 8px',
    borderRadius: '8px',
    cursor: 'pointer',
    '&:hover': {
      background: theme.palette.action.hover,
    },
  },
  stepRowActive: {
    background: '#f0f0ff',
  },
  stepNumber: {
    width: '26px',
    height: '26px',
    flexShrink: 0,
    borderRadius: '50%',
    background: '#8b8bf8',
    color: '#FFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 600,
  },
  stepNumberPassive: {
    background: '#d7d7fe',
    color: theme.palette.text.secondary,
  },
  stepLabel: {
    fontSize: theme.typography.pxToRem(14),
  },
  stepMeta: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.secondary,
  },
  stepChip: {
    fontVariantNumeric: 'tabular-nums',
  },
  stepDetail: {
    marginTop: '12px',
    padding: '12px 14px',
    borderRadius: '10px',
    background: '#fafaff',
  },
  stepDetailTitle: {
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: theme.palette.text.secondary,
  },
  stepDetailBody: {
    fontSize: theme.typography.pxToRem(13),
    whiteSpace: 'pre-wrap',
    marginTop: '4px',
  },
  contactRow: {
    fontSize: theme.typography.pxToRem(13),
  },
  replyRow: {
    borderLeft: '3px solid #8b8bf8',
    paddingLeft: '10px',
  },
  replyName: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 600,
  },
  replyText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.text.secondary,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  emptyText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.text.secondary,
  },
}));
